import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/cn';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { SegmentedTabs } from '@/components/ui/SegmentedTabs';
import {
  inputClass,
  labelClass,
  tableWrapClass,
  tableClass,
  theadClass,
  thClass,
  inlineErrorClass,
  inlineSuccessClass,
  settingsPanelClass,
} from '@/components/ui/inputStyles';

const PER_PAGE = 20;

type MpBrief = {
  id: number;
  name: string;
  public_id: string;
  publish_mode?: string;
};

type UserRow = {
  id: number;
  nickname: string;
  avatar_url: string;
  phone_masked: string;
  phone_bound: boolean;
  status: number;
  status_label: string;
  profile_pending: boolean;
  pending_nickname: string;
  pending_avatar_url: string;
  entry_count: number;
  pending_entry_count: number;
  agreements_version: string;
  agreements_accepted_at: string;
  phone_bound_at: string;
  created_at: string;
  updated_at: string;
};

type EntryBrief = {
  id: number;
  site_name: string;
  title_text: string;
  draft_status: string;
  status_label: string;
  sold_out: boolean;
  created_at: string;
};

type StatusFilter = 'all' | 'enabled' | 'disabled';
type PhoneFilter = 'all' | 'bound' | 'unbound';
type AuditFilter = 'all' | 'profile_pending' | 'entry_pending';

type Filters = {
  q: string;
  status: StatusFilter;
  phone: PhoneFilter;
  audit: AuditFilter;
};

const DEFAULT_FILTERS: Filters = {
  q: '',
  status: 'all',
  phone: 'all',
  audit: 'all',
};

function formatDateTime(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return '—';
  return s.length >= 16 ? s.slice(0, 16) : s;
}

function UserAvatar({ url, nickname }: { url: string; nickname: string }) {
  const letter = (nickname || '用').slice(0, 1);
  if (url) {
    return <img src={url} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-white shadow-sm" />;
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-100 to-slate-100 text-sm font-semibold text-teal-800 ring-2 ring-white shadow-sm">
      {letter}
    </div>
  );
}

function StatusBadge({ status }: { status: number }) {
  const ok = status === 1;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        ok ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200/80' : 'bg-rose-50 text-rose-800 ring-1 ring-rose-200/80',
      )}
    >
      {ok ? '正常' : '已禁用'}
    </span>
  );
}

function AuditBadges({ u }: { u: UserRow }) {
  const items: Array<{ key: string; label: string; cls: string }> = [];
  if (u.profile_pending) {
    items.push({ key: 'profile', label: '资料待审', cls: 'bg-amber-50 text-amber-900 ring-amber-200/80' });
  }
  if (u.pending_entry_count > 0) {
    items.push({
      key: 'entry',
      label: `发布待审 ${u.pending_entry_count}`,
      cls: 'bg-sky-50 text-sky-900 ring-sky-200/80',
    });
  }
  if (!items.length) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((b) => (
        <span key={b.key} className={cn('inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium ring-1', b.cls)}>
          {b.label}
        </span>
      ))}
    </div>
  );
}

export function MiniprogramUsersPage() {
  const { id: idParam } = useParams();
  const mpId = Number(idParam) || 0;
  const [mp, setMp] = useState<MpBrief | null>(null);
  const [items, setItems] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [draft, setDraft] = useState<Filters>(DEFAULT_FILTERS);
  const [applied, setApplied] = useState<Filters>(DEFAULT_FILTERS);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailUser, setDetailUser] = useState<UserRow | null>(null);
  const [detailEntries, setDetailEntries] = useState<EntryBrief[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actingId, setActingId] = useState(0);

  const hasActiveFilters = useMemo(
    () =>
      applied.q !== '' ||
      applied.status !== 'all' ||
      applied.phone !== 'all' ||
      applied.audit !== 'all',
    [applied],
  );

  const loadMp = useCallback(async () => {
    if (mpId < 1) return;
    try {
      const r = await apiFetch<{ ok: boolean; miniprogram?: MpBrief }>('get_miniprogram', {
        method: 'GET',
        query: { id: mpId },
      });
      if (r.ok && r.miniprogram) setMp(r.miniprogram);
      else setErr('小程序不存在或无权访问');
    } catch {
      setErr('加载小程序信息失败');
    }
  }, [mpId]);

  const load = useCallback(async () => {
    if (mpId < 1) return;
    setLoading(true);
    setErr('');
    try {
      const query: Record<string, string | number> = {
        miniprogram_id: mpId,
        page,
        per_page: PER_PAGE,
      };
      if (applied.q) query.q = applied.q;
      if (applied.status === 'enabled') query.status = 'enabled';
      if (applied.status === 'disabled') query.status = 'disabled';
      if (applied.phone === 'bound') query.phone_bound = 'bound';
      if (applied.phone === 'unbound') query.phone_bound = 'unbound';
      if (applied.audit === 'profile_pending') query.audit = 'profile_pending';
      if (applied.audit === 'entry_pending') query.audit = 'entry_pending';
      const r = await apiFetch<{ ok: boolean; items: UserRow[]; total: number }>('list_mp_users', {
        method: 'GET',
        query,
      });
      if (r.ok) {
        setItems(r.items || []);
        setTotal(r.total || 0);
      } else {
        setErr('加载用户列表失败');
      }
    } catch {
      setErr('加载用户列表失败');
    } finally {
      setLoading(false);
    }
  }, [mpId, page, applied]);

  useEffect(() => {
    void loadMp();
  }, [loadMp]);

  useEffect(() => {
    void load();
  }, [load]);

  function runSearch() {
    setPage(1);
    setApplied({ ...draft });
  }

  function resetFilters() {
    setDraft(DEFAULT_FILTERS);
    setApplied(DEFAULT_FILTERS);
    setPage(1);
  }

  async function openDetail(userId: number) {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailUser(null);
    setDetailEntries([]);
    try {
      const r = await apiFetch<{ ok: boolean; user: UserRow; recent_entries: EntryBrief[] }>('get_mp_user', {
        method: 'GET',
        query: { miniprogram_id: mpId, user_id: userId },
      });
      if (r.ok) {
        setDetailUser(r.user);
        setDetailEntries(r.recent_entries || []);
      }
    } catch {
      setErr('加载用户详情失败');
    } finally {
      setDetailLoading(false);
    }
  }

  async function deleteUser(user: Pick<UserRow, 'id' | 'nickname' | 'entry_count'>) {
    const name = user.nickname || `用户 #${user.id}`;
    const entryHint =
      user.entry_count > 0
        ? `将同时删除其发布的 ${user.entry_count} 条信息（含图片与审核记录），且不可恢复。`
        : '该用户暂无发布记录，删除后不可恢复。';
    if (
      !window.confirm(
        `确定删除「${name}」？\n\n${entryHint}\n\n删除后该用户需重新登录小程序才会再次出现在列表中。`,
      )
    ) {
      return;
    }
    setActingId(user.id);
    setMsg('');
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; deleted_entries?: number; detail?: string; err?: string }>(
        'delete_mp_user',
        {
          method: 'POST',
          body: JSON.stringify({ miniprogram_id: mpId, user_id: user.id }),
        },
      );
      if (!r.ok) {
        setErr(r.detail || r.err || '删除失败');
        return;
      }
      const n = typeof r.deleted_entries === 'number' ? r.deleted_entries : 0;
      setMsg(n > 0 ? `已删除用户，并移除 ${n} 条发布信息` : '已删除用户');
      if (detailOpen && detailUser?.id === user.id) {
        setDetailOpen(false);
        setDetailUser(null);
      }
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '删除失败');
    } finally {
      setActingId(0);
    }
  }

  async function runAction(
    action: 'approve_profile' | 'reject_profile' | 'enable' | 'disable',
    userId: number,
  ) {
    setActingId(userId);
    setMsg('');
    setErr('');
    try {
      if (action === 'approve_profile') {
        await apiFetch('approve_mp_user_profile', {
          method: 'POST',
          body: JSON.stringify({ miniprogram_id: mpId, user_id: userId }),
        });
        setMsg('已通过资料审核');
      } else if (action === 'reject_profile') {
        await apiFetch('reject_mp_user_profile', {
          method: 'POST',
          body: JSON.stringify({ miniprogram_id: mpId, user_id: userId }),
        });
        setMsg('已拒绝资料修改');
      } else {
        const status = action === 'enable' ? 1 : 0;
        await apiFetch('set_mp_user_status', {
          method: 'POST',
          body: JSON.stringify({ miniprogram_id: mpId, user_id: userId, status }),
        });
        setMsg(status === 1 ? '已启用用户' : '已禁用用户');
      }
      await load();
      if (detailOpen && detailUser?.id === userId) {
        await openDetail(userId);
      }
    } catch {
      setErr('操作失败');
    } finally {
      setActingId(0);
    }
  }

  if (mpId < 1) {
    return (
      <div>
        <PageHeader title="用户信息" />
        <p className={inlineErrorClass}>无效的小程序 ID</p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="space-y-6">
      <PageHeader
        title="用户信息"
        description={
          mp
            ? `管理「${mp.name}」（${mp.public_id}）的微信登录用户：实名、资料审核、发布统计与账号状态。数据按小程序隔离，不会与其他小程序混显。`
            : '加载中…'
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/miniprograms">
              <Button type="button" variant="secondary" size="sm">
                返回小程序列表
              </Button>
            </Link>
            <Link to={`/miniprograms/${mpId}?view=config`}>
              <Button type="button" variant="ghost" size="sm">
                参数配置
              </Button>
            </Link>
          </div>
        }
      />

      {msg ? <div className={inlineSuccessClass}>{msg}</div> : null}
      {err ? <div className={inlineErrorClass}>{err}</div> : null}

      <Card title="筛选">
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className={labelClass + ' block'}>
              <span className="mb-1.5 block">关键词</span>
              <input
                className={inputClass}
                value={draft.q}
                onChange={(e) => setDraft((f) => ({ ...f, q: e.target.value }))}
                placeholder="用户 ID / 昵称 / 手机号"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runSearch();
                }}
              />
            </label>
            <label className={labelClass + ' block'}>
              <span className="mb-1.5 block">账号状态</span>
              <select
                className={inputClass}
                value={draft.status}
                onChange={(e) => setDraft((f) => ({ ...f, status: e.target.value as StatusFilter }))}
              >
                <option value="all">全部</option>
                <option value="enabled">正常</option>
                <option value="disabled">已禁用</option>
              </select>
            </label>
            <label className={labelClass + ' block'}>
              <span className="mb-1.5 block">实名认证</span>
              <select
                className={inputClass}
                value={draft.phone}
                onChange={(e) => setDraft((f) => ({ ...f, phone: e.target.value as PhoneFilter }))}
              >
                <option value="all">全部</option>
                <option value="bound">已绑定手机</option>
                <option value="unbound">未绑定手机</option>
              </select>
            </label>
            <label className={labelClass + ' block'}>
              <span className="mb-1.5 block">待办筛选</span>
              <select
                className={inputClass}
                value={draft.audit}
                onChange={(e) => setDraft((f) => ({ ...f, audit: e.target.value as AuditFilter }))}
              >
                <option value="all">不限</option>
                <option value="profile_pending">资料待审</option>
                <option value="entry_pending">发布待审</option>
              </select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="primary" onClick={runSearch}>
              搜索
            </Button>
            <Button type="button" variant="secondary" onClick={resetFilters} disabled={!hasActiveFilters && draft.q === ''}>
              重置
            </Button>
            {hasActiveFilters ? (
              <span className="text-xs text-slate-500">已应用筛选条件</span>
            ) : null}
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-slate-500">快捷筛选</p>
            <SegmentedTabs<AuditFilter>
              ariaLabel="待办快捷筛选"
              value={draft.audit}
              onChange={(id) => {
                const next = { ...draft, audit: id };
                setDraft(next);
                setApplied(next);
                setPage(1);
              }}
              items={[
                { id: 'all', label: '全部用户' },
                { id: 'profile_pending', label: '资料待审' },
                { id: 'entry_pending', label: '发布待审' },
              ]}
            />
          </div>
        </div>
      </Card>

      <Card
        title="用户列表"
        titleExtra={<span className="text-sm font-normal text-slate-500">共 {total} 人</span>}
      >
        {loading ? (
          <Spinner label="加载用户" />
        ) : items.length === 0 ? (
          <EmptyState>
            {hasActiveFilters ? '没有符合筛选条件的用户，可尝试放宽条件或重置筛选。' : '暂无注册用户，用户在微信小程序登录后将出现在此列表。'}
          </EmptyState>
        ) : (
          <>
            <div className={tableWrapClass}>
              <table className={tableClass}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>用户</th>
                    <th className={thClass}>ID</th>
                    <th className={thClass}>手机号</th>
                    <th className={thClass}>状态</th>
                    <th className={thClass}>待办</th>
                    <th className={thClass}>发布</th>
                    <th className={thClass}>注册时间</th>
                    <th className={cn(thClass, 'text-right')}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((u) => {
                    const busy = actingId === u.id;
                    const rowHighlight = u.profile_pending || u.pending_entry_count > 0;
                    return (
                      <tr
                        key={u.id}
                        className={cn(
                          'border-t border-slate-100 text-sm text-slate-800 transition-colors hover:bg-slate-50/80',
                          rowHighlight && 'border-l-[3px] border-l-amber-400 bg-amber-50/20',
                          u.status !== 1 && 'opacity-75',
                        )}
                      >
                        <td className="px-4 py-3">
                          <div className="flex min-w-[160px] items-center gap-3">
                            <UserAvatar url={u.avatar_url} nickname={u.nickname} />
                            <div className="min-w-0">
                              <p className="truncate font-medium text-slate-900">{u.nickname || '未设置昵称'}</p>
                              {u.profile_pending && u.pending_nickname ? (
                                <p className="truncate text-xs text-amber-700">待审：{u.pending_nickname}</p>
                              ) : (
                                <p className="text-xs text-slate-500">更新 {formatDateTime(u.updated_at)}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-600">{u.id}</td>
                        <td className="px-4 py-3">
                          {u.phone_bound ? (
                            <span className="text-slate-800">{u.phone_masked}</span>
                          ) : (
                            <span className="text-slate-400">未绑定</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={u.status} />
                        </td>
                        <td className="px-4 py-3 min-w-[120px]">
                          <AuditBadges u={u} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-medium text-slate-900">{u.entry_count}</span>
                          <span className="text-slate-500"> 条</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">
                          {formatDateTime(u.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void openDetail(u.id)}>
                              详情
                            </Button>
                            {u.profile_pending ? (
                              <>
                                <Button
                                  type="button"
                                  variant="primary"
                                  size="sm"
                                  disabled={busy}
                                  onClick={() => void runAction('approve_profile', u.id)}
                                >
                                  通过
                                </Button>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  disabled={busy}
                                  onClick={() => void runAction('reject_profile', u.id)}
                                >
                                  拒绝
                                </Button>
                              </>
                            ) : null}
                            {u.status === 1 ? (
                              <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                disabled={busy}
                                onClick={() => void runAction('disable', u.id)}
                              >
                                禁用
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                disabled={busy}
                                onClick={() => void runAction('enable', u.id)}
                              >
                                启用
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="danger"
                              size="sm"
                              disabled={busy}
                              onClick={() => void deleteUser(u)}
                            >
                              删除
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationBar
              className="mt-4"
              page={page}
              totalPages={totalPages}
              total={total}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
          </>
        )}
      </Card>

      <Modal open={detailOpen} title="用户详情" onClose={() => setDetailOpen(false)} wide="xl">
        {detailLoading || !detailUser ? (
          <Spinner label="加载详情" />
        ) : (
          <div className="space-y-5">
            <div className={settingsPanelClass + ' flex flex-wrap gap-4'}>
              <UserAvatar url={detailUser.avatar_url} nickname={detailUser.nickname} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold text-slate-900">{detailUser.nickname || '未设置昵称'}</h3>
                  <StatusBadge status={detailUser.status} />
                </div>
                <p className="mt-1 text-sm text-slate-600">用户 ID：{detailUser.id}</p>
                <p className="text-sm text-slate-600">
                  手机：{detailUser.phone_bound ? detailUser.phone_masked : '未绑定'}
                  {detailUser.phone_bound_at ? ` · 绑定于 ${formatDateTime(detailUser.phone_bound_at)}` : ''}
                </p>
                <p className="text-sm text-slate-600">
                  协议 {detailUser.agreements_version || '—'}
                  {detailUser.agreements_accepted_at
                    ? ` · 同意于 ${formatDateTime(detailUser.agreements_accepted_at)}`
                    : ''}
                </p>
                <p className="text-sm text-slate-600">
                  发布 {detailUser.entry_count} 条
                  {detailUser.pending_entry_count > 0 ? ` · 待审 ${detailUser.pending_entry_count} 条` : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 self-start">
                {detailUser.profile_pending ? (
                  <>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      disabled={actingId === detailUser.id}
                      onClick={() => void runAction('approve_profile', detailUser.id)}
                    >
                      通过资料
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={actingId === detailUser.id}
                      onClick={() => void runAction('reject_profile', detailUser.id)}
                    >
                      拒绝资料
                    </Button>
                  </>
                ) : null}
                {detailUser.status === 1 ? (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    disabled={actingId === detailUser.id}
                    onClick={() => void runAction('disable', detailUser.id)}
                  >
                    禁用账号
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={actingId === detailUser.id}
                    onClick={() => void runAction('enable', detailUser.id)}
                  >
                    启用账号
                  </Button>
                )}
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  disabled={actingId === detailUser.id}
                  onClick={() => void deleteUser(detailUser)}
                >
                  删除用户
                </Button>
              </div>
            </div>

            {detailUser.profile_pending ? (
              <div className="rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950">
                <p className="font-medium">资料待审核</p>
                {detailUser.pending_nickname ? <p className="mt-1">待审昵称：{detailUser.pending_nickname}</p> : null}
                {detailUser.pending_avatar_url ? (
                  <p className="mt-2 flex items-center gap-2">
                    待审头像：
                    <img src={detailUser.pending_avatar_url} alt="" className="h-12 w-12 rounded-full object-cover ring-2 ring-white" />
                  </p>
                ) : null}
              </div>
            ) : null}

            <div>
              <h4 className="mb-3 text-sm font-semibold text-slate-800">最近发布（最多 20 条）</h4>
              {detailEntries.length === 0 ? (
                <EmptyState className="py-10">该用户暂无发布记录</EmptyState>
              ) : (
                <div className={tableWrapClass}>
                  <table className={tableClass}>
                    <thead className={theadClass}>
                      <tr>
                        <th className={thClass}>ID</th>
                        <th className={thClass}>标题</th>
                        <th className={thClass}>站点</th>
                        <th className={thClass}>状态</th>
                        <th className={thClass}>时间</th>
                        <th className={cn(thClass, 'text-right')}>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailEntries.map((e) => (
                        <tr key={e.id} className="border-t border-slate-100 text-sm hover:bg-slate-50/60">
                          <td className="px-4 py-2.5 font-mono text-xs">{e.id}</td>
                          <td className="max-w-[200px] truncate px-4 py-2.5 font-medium text-slate-900">
                            {e.title_text || '无标题'}
                          </td>
                          <td className="px-4 py-2.5 text-slate-600">{e.site_name || '—'}</td>
                          <td className="px-4 py-2.5">
                            <span
                              className={cn(
                                'rounded-md px-2 py-0.5 text-xs font-medium',
                                e.draft_status === 'awaiting_review'
                                  ? 'bg-amber-50 text-amber-900'
                                  : e.draft_status === 'review_rejected'
                                    ? 'bg-rose-50 text-rose-800'
                                    : 'bg-emerald-50 text-emerald-800',
                              )}
                            >
                              {e.status_label}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">
                            {formatDateTime(e.created_at)}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <Link
                              to={`/miniprograms/${mpId}?view=content&entry_id=${e.id}`}
                              className="text-xs font-medium text-teal-700 hover:underline"
                            >
                              查看
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
